import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';
import { ProfileMenu } from '@/components/layouts/ProfileMenu';

const LOGO = '/figma-assets/logo-white.png';

/**
 * Routes that live under the "My Portfolio" workspace — the sub-nav tabs. While
 * the user is on any of them, the top-level "My Portfolio" link stays active.
 */
const PORTFOLIO_SECTION_PATHS: string[] = [
  ROUTES.portfolios,
  ROUTES.portfolioList,
  ROUTES.holdings,
  ROUTES.performance,
  ROUTES.transactions,
  ROUTES.incomeCalendar,
  ROUTES.accountInfo,
];

/** Primary nav links. */
const NAV_ITEMS = [
  {
    to: ROUTES.portfolios,
    key: 'portalNav.myPortfolio',
    enabled: true,
    section: PORTFOLIO_SECTION_PATHS,
  },
  // Mutual Fund tab hidden per request (2026-09-21) — pending resolution.
  // { to: ROUTES.mutualFund, key: 'portalNav.mutualFund', enabled: true },
] as const;

/**
 * BFM Online (Barita) portal top navigation: black bar with the white logo, the
 * section links, and the profile menu on the right. The profile menu links to
 * the Profile page and signs out. The Relationship Manager now lives in the
 * dashboard title row.
 */
export function PortalNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <header className="relative z-20 flex h-[120px] w-full items-center bg-barita-ink px-6 md:px-[60px]">
      <Link to={ROUTES.home} aria-label={t('portalNav.home')} className="shrink-0">
        <img
          src={LOGO}
          alt="Barita Fund Managers Limited"
          className="h-[90px] w-[158px] object-contain"
        />
      </Link>

      <nav className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-10 lg:flex">
        {NAV_ITEMS.map((item) =>
          item.enabled ? (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'border-b-2 border-transparent pb-1 text-[20px] font-normal leading-[24px] text-barita-mist transition hover:text-white',
                  (isActive || ('section' in item && item.section.includes(pathname))) &&
                    'border-white text-white',
                )
              }
            >
              {t(item.key)}
            </NavLink>
          ) : (
            <a
              key={item.key}
              href={item.to}
              aria-disabled
              title={t('portalNav.comingSoon')}
              className="cursor-not-allowed border-b-2 border-transparent pb-1 text-[20px] font-normal leading-[24px] text-barita-mist/70"
              onClick={(e) => e.preventDefault()}
            >
              {t(item.key)}
            </a>
          ),
        )}
      </nav>

      <div className="ml-auto flex items-center">
        <ProfileMenu />
      </div>
    </header>
  );
}
